import QRCode from "qrcode";
import { jsPDF } from "jspdf";

export const BRAND = "thecellarbook.pt";
export const ROLL_H = 29;
export const LABEL_SIZES = [40, 50, 60, 70, 80].map((w) => ({ w, h: ROLL_H }));
export const DEFAULT_SIZE = { w: 50, h: ROLL_H };
export const EXTRA_SIZES = [
  { w: 40, h: 12, note: "Brother 12 mm roll" },
  { w: 51, h: 12, note: "12 mm roll, longer" },
  { w: 23, h: 23, note: "Brother square" },
  { w: 51, h: 26, note: "Small sticker" },
  { w: 62, h: 29, note: "Brother 62 × 29" },
];
export const isRoll29 = ({ w, h }) => h === ROLL_H && LABEL_SIZES.some((s) => s.w === w);

const PT = 0.3528;
export const labelWine = (l) => (l.kind === "box" ? `Box ${l.box_number || l.barcode}` : l.wine || [l.title, l.sub].filter(Boolean).join(" · "));
export const shortUrl = (code) => `${window.location.origin}/Q/${code}`.toUpperCase();
export const boxUrl = shortUrl;
export const qrText = (l) => shortUrl(l.barcode);
export const todayLabel = () => new Date().toLocaleDateString("pt-PT");
export const qrDataUrl = (l) => QRCode.toDataURL(qrText(l), { margin: 0, errorCorrectionLevel: "M", width: 400 });
export const qrSvg = (l, sizeMm) => {
  const { modules } = QRCode.create(qrText(l), { errorCorrectionLevel: "M" });
  const n = modules.size;
  const raw = sizeMm / n;
  const m = raw >= 0.6 ? raw : Math.max(Math.floor(raw / (25.4 / 300)), 1) * (25.4 / 300);
  let d = "";
  for (let r = 0; r < n; r += 1) for (let c = 0; c < n; c += 1) if (modules.get(r, c)) d += `M${c} ${r}h1v1h-1z`;
  return { mm: m * n, svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges"><path d="${d}" fill="#000"/></svg>` };
};

const splitVintage = (l) => {
  const t = labelWine(l);
  const m = t.match(/^(.*?)\s+(\d{4}|NV)$/);
  return m ? [m[1], m[2]] : [t, ""];
};

const measureDoc = new jsPDF({ unit: "mm" });
const width = (text, pt, bold) => {
  measureDoc.setFont("helvetica", bold ? "bold" : "normal");
  measureDoc.setFontSize(pt);
  return measureDoc.getTextWidth(text);
};
export const fitPt = (text, maxW, size, min, bold = true) => {
  let s = size;
  while (s > min && width(text, s, bold) > maxW) s -= 0.25;
  return s;
};
const ellipsize = (text, maxW, pt, bold) => {
  if (width(text, pt, bold) <= maxW) return text;
  let t = text;
  while (t.length > 1 && width(`${t}…`, pt, bold) > maxW) t = t.slice(0, -1).trimEnd();
  return t === text ? t : `${t}…`;
};
const wrap = (text, maxW, pt) => {
  const out = [];
  text.split(/\s+/).forEach((word) => {
    const last = out[out.length - 1];
    if (last !== undefined && width(`${last} ${word}`, pt, true) <= maxW) out[out.length - 1] = `${last} ${word}`;
    else out.push(word);
  });
  return out;
};

// 29 mm roll: fixed QR 20 x 20 mm left, name on up to 2 lines, vintage, date, website
const roll29Lines = (l, { w, h }) => {
  const q = 20;
  const L = { q, qx: 3.5, qy: (h - q) / 2, x: 3.5 + q + 2.5, top: 3.4, bottom: h - 3.4 };
  L.maxW = w - L.x - 2;
  const [name, vintage] = splitVintage(l);
  const upper = name.toUpperCase();
  let pn = 11;
  while (pn > 6.5 && (wrap(upper, L.maxW, pn).length > 2 || wrap(upper, L.maxW, pn).some((x) => width(x, pn, true) > L.maxW))) pn -= 0.25;
  let nameLines = wrap(upper, L.maxW, pn);
  if (nameLines.length > 2 || nameLines.some((x) => width(x, pn, true) > L.maxW)) {
    nameLines = [ellipsize(nameLines[0], L.maxW, pn, true), ...(nameLines.length > 1 ? [ellipsize(nameLines.slice(1).join(" "), L.maxW, pn, true)] : [])];
  }
  const groups = [nameLines.map((text, i) => ({ key: `name-${i}`, text, pt: pn, bold: true }))];
  if (vintage) groups.push([{ key: "vintage", text: vintage, pt: fitPt(vintage, L.maxW, 12, 6), bold: true }]);
  const pb = fitPt(BRAND, L.maxW, Math.min(8, Math.max(pn, 6)), 4);
  groups.push([{ key: "date", text: todayLabel(), pt: fitPt(todayLabel(), L.maxW, Math.min(7, pb), 4, false), bold: false, grey: true }]);
  groups.push([{ key: "brand", text: BRAND, pt: pb, bold: true }]);
  const gh = (g) => g.reduce((a, x, i) => a + x.pt * PT * (i ? 1.15 : 1), 0);
  const used = groups.reduce((a, g) => a + gh(g), 0);
  const gap = (L.bottom - L.top - used) / (groups.length - 1);
  let y = L.top;
  const lines = [];
  groups.forEach((g) => {
    g.forEach((x, i) => {
      y += i ? x.pt * PT * 0.15 : 0;
      lines.push({ ...x, y: y + 0.8 * x.pt * PT });
      y += x.pt * PT;
    });
    y += gap;
  });
  return { L, lines };
};

// Other sizes: QR + name/year, date, website, sized to the label
const isTall = ({ w, h }) => w / h < 1.6;
const otherLayout = ({ w, h }) => {
  const pad = 1.2;
  if (isTall({ w, h })) {
    const k = Math.min(Math.max(w / 23, 1), 2, (w - 2 * pad) / 15);
    const q = Math.min(w - 2 * pad, h - 2 * pad - 7 * k);
    if (q >= 9) return { tall: true, pad, q, qx: (w - q) / 2, qy: pad, k, maxW: w - 2 * pad, top: pad + q + 0.6, x: w / 2 };
  }
  const q = Math.min(h - 2 * pad, w * (w < 30 ? 0.55 : 0.42), Math.max(10, h * 0.6));
  const x = pad + q + 1.4;
  const maxW = w - x - pad;
  const k = Math.max(Math.min(h / 12, 1.8, maxW / 16), 0.6);
  return { tall: false, pad, q, qx: pad, qy: pad, k, x, maxW, top: pad };
};
const fitWine = (text, maxW, pt) => {
  const m = text.match(/^(.*?)(\s+(?:\d{4}|NV))$/);
  if (!m) return ellipsize(text, maxW, pt, true);
  let t = m[1];
  while (t.length > 1 && width(`${t}${t === m[1] ? "" : "…"}${m[2]}`, pt, true) > maxW) t = t.slice(0, -1).trimEnd();
  return `${t}${t === m[1] ? "" : "…"}${m[2]}`;
};
const otherLines = (l, size) => {
  const L = otherLayout(size);
  const lines = [
    { key: "name", text: labelWine(l).toUpperCase(), bold: true, pt: (L.tall ? 6 : 7.5) * L.k },
    { key: "date", text: todayLabel(), grey: true, pt: (L.tall ? 4.5 : 5) * L.k },
    { key: "brand", text: BRAND, bold: true, pt: (L.tall ? 5.5 : 6.5) * L.k },
  ];
  lines.forEach((x) => {
    x.pt = fitPt(x.text, L.maxW, x.pt, x.pt * 0.5, x.bold);
    if (x.key === "name") x.text = fitWine(x.text, L.maxW, x.pt);
  });
  const avail = size.h - L.pad - L.top;
  const used = lines.reduce((a, x) => a + x.pt * PT, 0);
  const gap = (avail - used) / (lines.length - 1);
  let y = L.top;
  lines.forEach((x) => {
    x.y = y + 0.8 * x.pt * PT;
    y += x.pt * PT + gap;
  });
  return { L, lines };
};

// Lines with font size (pt) and baseline y (mm), shared by the print sheet, the preview and the PDF
export const labelLines = (l, size) => (isRoll29(size) ? roll29Lines(l, size) : otherLines(l, size));

const DOT = 25.4 / 300;
const drawQr = (doc, text, x, y, size) => {
  const { modules } = QRCode.create(text, { errorCorrectionLevel: "M" });
  const m = Math.max(Math.floor(size / modules.size / DOT), 1) * DOT;
  const off = (size - m * modules.size) / 2;
  x = Math.round((x + off) / DOT) * DOT;
  y = Math.round((y + off) / DOT) * DOT;
  doc.setFillColor(0, 0, 0);
  for (let r = 0; r < modules.size; r += 1) {
    for (let c = 0; c < modules.size; c += 1) {
      if (modules.get(r, c)) doc.rect(x + c * m, y + r * m, m, m, "F");
    }
  }
};

export const labelsPdf = async (labels, size = DEFAULT_SIZE) => {
  const { w, h } = size;
  const orientation = w >= h ? "landscape" : "portrait";
  const doc = new jsPDF({ unit: "mm", format: [w, h], orientation });
  labels.forEach((l, i) => {
    if (i) doc.addPage([w, h], orientation);
    const { L, lines } = labelLines(l, size);
    drawQr(doc, qrText(l), L.qx, L.qy, L.q);
    const align = L.tall ? { align: "center" } : {};
    lines.forEach((x) => {
      doc.setFont("helvetica", x.bold ? "bold" : "normal");
      doc.setFontSize(x.pt);
      doc.setTextColor(x.grey ? 90 : 0);
      doc.text(x.text, L.x, x.y, align);
    });
  });
  doc.setProperties({ title: "The Cellar Book labels" });
  return doc.output("blob");
};
